import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import Box from '@mui/joy/Box';
import Alert from '@mui/joy/Alert';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import Warning from '@mui/icons-material/Warning';
import Close from '@mui/icons-material/Close';
import { GET_CLIENT } from '../../gql/subscription.queries';
import { CREATE_PORTAL_SESSION } from '../../gql/subscription.mutations';
import { useFeedback } from '../feedback/Service';

const DISMISSED_KEY = 'payment_failure_banner_dismissed';

export default function PaymentFailureBanner() {
  const { snackbar } = useFeedback();
  const [dismissed, setDismissed] = useState(false);
  
  const { data: clientData } = useQuery(GET_CLIENT, {
    pollInterval: 60000 // Poll every minute to catch payment updates
  });
  
  const [createPortalSession, { loading }] = useMutation(CREATE_PORTAL_SESSION);

  const client = clientData?.getClient;
  const creditsExpireAt = client?.creditsExpireAt;

  // Check if was dismissed in this session
  useEffect(() => {
    const dismissedUntil = sessionStorage.getItem(DISMISSED_KEY);
    if (dismissedUntil) {
      const dismissedDate = new Date(dismissedUntil);
      if (dismissedDate > new Date()) {
        setDismissed(true);
      } else {
        sessionStorage.removeItem(DISMISSED_KEY);
      }
    }
  }, []);

  // Reset dismissed state if creditsExpireAt changes (new failure)
  useEffect(() => {
    if (creditsExpireAt) {
      const dismissedUntil = sessionStorage.getItem(DISMISSED_KEY);
      if (dismissedUntil !== creditsExpireAt) {
        setDismissed(false);
      }
    }
  }, [creditsExpireAt]);

  const getDaysUntil = (dateString) => {
    if (!dateString) return 0;
    const diff = new Date(dateString) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const handleUpdatePayment = async () => {
    try {
      const { data } = await createPortalSession();
      if (data?.createPortalSession?.url) {
        window.location.href = data.createPortalSession.url;
      }
    } catch (error) {
      snackbar({ text: error.message || 'Failed to open billing portal', type: 'error' });
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Remember dismissal for this creditsExpireAt value
    if (creditsExpireAt) {
      sessionStorage.setItem(DISMISSED_KEY, creditsExpireAt);
    }
  };

  // Don't show if no payment failure or if dismissed
  if (!creditsExpireAt || dismissed) {
    return null;
  }

  const daysLeft = getDaysUntil(creditsExpireAt);
  const isUrgent = daysLeft <= 3;

  return (
    <Alert
      variant="soft"
      color={isUrgent ? 'danger' : 'warning'}
      startDecorator={<Warning />}
      endDecorator={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="sm"
            variant="solid"
            color={isUrgent ? 'danger' : 'warning'}
            onClick={handleUpdatePayment}
            loading={loading}
          >
            Update Payment
          </Button>
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={handleDismiss}
          >
            <Close />
          </IconButton>
        </Box>
      }
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        borderRadius: 0
      }}
    >
      <Box>
        <Typography level="title-sm" color={isUrgent ? 'danger' : 'warning'}>
          Payment Failed
        </Typography>
        <Typography level="body-sm">
          {daysLeft === 0 
            ? 'Your credits will expire today! Update your payment method immediately.'
            : daysLeft === 1
            ? 'Your credits will expire tomorrow! Update your payment method now.'
            : `Update your payment method within ${daysLeft} days or lose your credits.`
          }
        </Typography>
      </Box>
    </Alert>
  );
}
